import React, {FC, memo} from "react";
import {Button, Input, Space} from "antd";
import {ArrayDataOptions, FilterOption, Maybe} from "../../generated/graphql";

// sc-3040 follow-up — Generic three-mode filter for text columns that should also
// support "field present" / "field empty" quick filters. The two sentinel values
// are written into `FilterOption.values[0]` and decoded by the corresponding
// backend filter helper. Modelled after the credit-branch in FilterDropdownNumber.tsx.
//
// The placeholder is passed in as a prop instead of being looked up via the i18n
// proxy — the proxy holds state across get/apply pairs and a dynamic `[dataIndex]`
// lookup can pollute that state between renders. Static prop is safer.

type FilterDropdownPresenceProps = {
    dataIndex: string;
    options?: Maybe<ArrayDataOptions>;
    setOptions?: (options: ArrayDataOptions) => void;
    setDropdownOpen?: (dropdownOpen: string | null) => void;
    table?: string;
    presentSentinel: string;
    absentSentinel: string;
    presentLabel: string;
    absentLabel: string;
    placeholder: string;
};

const PANEL_WIDTH = 240;

const FilterDropdownPresence: FC<FilterDropdownPresenceProps> = ({
    dataIndex,
    options,
    setOptions,
    setDropdownOpen,
    table,
    presentSentinel,
    absentSentinel,
    presentLabel,
    absentLabel,
    placeholder,
}) => {
    const currentFilter = options?.filter?.find((filter) => filter.column === dataIndex);
    const currentValue = currentFilter?.values[0];

    const [searchKey, setSearchKey] = React.useState<string>(
        currentValue && currentValue !== presentSentinel && currentValue !== absentSentinel ? currentValue : "",
    );

    const setFilter = (value: string) => {
        if (options?.filter?.find((filter) => filter.column === dataIndex)) {
            const updatedFilters = options.filter.map((filter) =>
                filter.column === dataIndex
                    ? {
                          ...filter,
                          values: [value],
                      }
                    : filter,
            );

            setOptions?.({...options, filter: updatedFilters});
            setDropdownOpen?.(null);

            return;
        }

        const newFilter: FilterOption = {
            column: dataIndex,
            values: [value],
            table: table ?? "payment",
        };

        setOptions?.({...options, filter: [...(options?.filter ?? []), newFilter]});
        setDropdownOpen?.(null);
    };

    const resetFilter = () => {
        const updatedFilters = options?.filter?.filter((el) => el.column !== dataIndex);

        setOptions?.({...options, filter: updatedFilters});
        setDropdownOpen?.(null);
    };

    return (
        <div style={{padding: 8, width: PANEL_WIDTH}}>
            <Input
                allowClear
                autoFocus
                placeholder={placeholder}
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                onPressEnter={() => {
                    if (searchKey) setFilter(searchKey);
                }}
                style={{marginBottom: 8}}
            />
            <Space direction="vertical" style={{width: "100%"}} size={4}>
                <Button
                    type="primary"
                    block
                    size="small"
                    disabled={!searchKey}
                    onClick={() => setFilter(searchKey)}
                >
                    {"Suchen"}
                </Button>
                <Button
                    block
                    size="small"
                    type={currentValue === presentSentinel ? "primary" : "default"}
                    onClick={() => setFilter(presentSentinel)}
                >
                    {presentLabel}
                </Button>
                <Button
                    block
                    size="small"
                    type={currentValue === absentSentinel ? "primary" : "default"}
                    onClick={() => setFilter(absentSentinel)}
                >
                    {absentLabel}
                </Button>
                <Button
                    block
                    size="small"
                    danger
                    disabled={!currentFilter}
                    onClick={resetFilter}
                >
                    {"Filter entfernen"}
                </Button>
            </Space>
        </div>
    );
};

export default memo(FilterDropdownPresence);
